import axios from 'axios'
import { Message, Loading } from 'element-ui'
import store from '../store'
import { getToken, removeToken } from '@/utils/auth'
import router from '../router'

// 创建axios实例
const service = axios.create({
  baseURL: process.env.VUE_APP_BASE_API,
  timeout: 10 * 1000,
  headers: {
    'Content-Type': 'application/json;charset=utf-8'
  }
})

// 加载动画
let loadingInstance = null
// 标记是否正在登出，防止无限循环
let isLoggingOut = false

// 请求拦截器
service.interceptors.request.use(
  config => {
    // 显示加载动画
    if (config.loading) {
      loadingInstance = Loading.service({
        lock: true,
        text: '加载中...',
        background: 'rgba(0, 0, 0, 0.7)'
      })
    }
    
    // 如果有token，添加到请求头
    if (getToken()) {
      config.headers['Authorization'] = `Bearer ${getToken()}`
    }
    return config
  },
  error => {
    if (loadingInstance) {
      loadingInstance.close()
    }
    Message.error('请求错误: ' + error.message)
    return Promise.reject(error)
  }
)

// 响应拦截器
service.interceptors.response.use(
  response => {
    if (loadingInstance) {
      loadingInstance.close()
    }
    
    const res = response.data
    
    if (res.code !== 200) {
      Message({
        message: res.message || res.msg || '请求失败',
        type: 'error',
        duration: 5 * 1000,
      });
      
      // 处理401错误，添加防无限循环机制
      if (res.code === 401 && !isLoggingOut) {
        handle401Error()
      }
      
      return Promise.reject(new Error(res.message || 'Error'))
    } else {
      return res.data ? res.data : res
    }
  },
  error => {
    if (loadingInstance) {
      loadingInstance.close()
    }
    
    let errorMsg = '请求失败，请稍后重试'
    if (error.response) {
      switch (error.response.status) {
        case 401:
          // 处理401错误，添加防无限循环机制
          if (!isLoggingOut) {
            handle401Error()
          }
          errorMsg = '未授权，请重新登录'
          break
        case 404:
          errorMsg = '请求的资源不存在'
          break
        case 500:
          errorMsg = '服务器内部错误'
          break
        default:
          errorMsg = `请求错误: ${error.response.status}`
      }
    } else if (error.message.includes('timeout')) {
      errorMsg = '请求超时'
    }
    
    // 如果不是401错误才显示消息，避免重复提示
    if (error.response?.status !== 401) {
      Message.error(errorMsg)
    }
    
    return Promise.reject(error)
  }
)

// 专门处理401错误的函数
function handle401Error() {
  // 标记正在登出
  isLoggingOut = true
  
  Message.error('无效的会话，或者登录已过期，请重新登录。');
  
  // 先清除本地token，避免后续请求继续携带无效token
  removeToken()
  
  // 使用setTimeout避免当前Promise链影响
  setTimeout(() => {
    // 如果登出需要调用接口，这里可以判断当前请求是否是登出请求
    // 简单处理：直接清除用户状态并跳转登录页
    store.commit('user/CLEAR_USER_INFO') // 假设这个action只清除状态，不发请求
    
    // 跳转到登录页，记录当前路径用于登录后返回
    const currentPath = router.currentRoute.fullPath
    if (currentPath !== '/login') {
      router.replace({
        path: '/login',
        query: { redirect: currentPath }
      })
    }
    
    // 重置标记
    isLoggingOut = false
  }, 1000)
}

export default service
